import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  computed,
  signal,
  ElementRef,
  inject,
  HostListener
} from '@angular/core';
import { AcaFaIcon, AcaUiModule } from '@aca/ui';
import { faChevronDown, faCheck } from '@fortawesome/pro-regular-svg-icons';

/**
 * Generic option type for the shared dropdown.
 * Consumers can provide any object type or primitive (string/number).
 * Use displayFn and valueFn to extract display text and value.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DropdownOption = Record<string, any> | string | number;

/**
 * SharedDropdownComponent - A reusable dropdown component for use across all MFEs.
 *
 * Features:
 * - Generic option support via displayFn and valueFn
 * - Pill, standard, or structure styling variants
 * - Customizable border color and radius
 * - Click-outside detection to close dropdown
 * - Keyboard support (Escape to close)
 * - Accessibility attributes (ARIA)
 * - Optional label display
 * - Checkmark on selected item
 *
 * @example
 * ```html
 * <!-- Basic usage with pill variant -->
 * <aca-shared-dropdown
 *   [options]="myOptions"
 *   [selectedValue]="currentValue"
 *   [displayFn]="getDisplayText"
 *   [valueFn]="getValue"
 *   variant="pill"
 *   placeholder="Select an option"
 *   (selectionChanged)="onSelect($event)">
 * </aca-shared-dropdown>
 *
 * <!-- Tier-selector style with custom border -->
 * <aca-shared-dropdown
 *   [options]="structures"
 *   variant="pill"
 *   borderColor="var(--aca-blue)"
 *   borderRadius="rounded-full"
 *   placeholder="Select structure"
 *   (selectionChanged)="onSelect($event)">
 * </aca-shared-dropdown>
 *
 * <!-- Structure card style -->
 * <aca-shared-dropdown
 *   [options]="trimOptions"
 *   variant="structure"
 *   borderColor="var(--color-gray-300)"
 *   borderRadius="var(--radius-sm)"
 *   placeholder="Select Trim"
 *   (selectionChanged)="onTrimSelected($event)">
 * </aca-shared-dropdown>
 *
 * <!-- Custom styling -->
 * <aca-shared-dropdown
 *   [options]="options"
 *   variant="pill"
 *   borderColor="#ff0000"
 *   borderRadius="0.25rem"
 *   (selectionChanged)="onSelect($event)">
 * </aca-shared-dropdown>
 *
 * <!-- Right-aligned dropdown (for items near right edge of screen) -->
 * <aca-shared-dropdown
 *   [options]="filterOptions"
 *   variant="pill"
 *   [alignRight]="true"
 *   (selectionChanged)="onSelect($event)">
 * </aca-shared-dropdown>
 * ```
 */
@Component({
  selector: 'aca-shared-dropdown',
  standalone: true,
  templateUrl: './shared-dropdown.component.html',
  styleUrls: ['./shared-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AcaUiModule]
})
export class SharedDropdownComponent {
  private readonly elementRef = inject(ElementRef);

  /**
   * Array of options to display in the dropdown.
   * Can be any object type - use displayFn and valueFn to extract values.
   */
  options = input<DropdownOption[]>([]);

  /**
   * Currently selected value (compared using valueFn result).
   */
  selectedValue = input<string | number | null | undefined>(null);

  /**
   * Placeholder text when no option is selected.
   */
  placeholder = input<string>('Select...');

  /**
   * Whether the dropdown is disabled.
   */
  disabled = input<boolean>(false);

  /**
   * Function to extract display text from an option.
   * Defaults to returning the option as-is (for string arrays).
   */
  displayFn = input<(option: DropdownOption) => string>((option) => String(option));

  /**
   * Function to extract the value from an option for comparison.
   * Defaults to returning the option as-is (for string arrays).
   */
  valueFn = input<(option: DropdownOption) => string | number | null | undefined>((option) => String(option));

  /**
   * Whether to show a label above the dropdown.
   */
  showLabel = input<boolean>(false);

  /**
   * Label text to display above the dropdown.
   */
  label = input<string>('');

  /**
   * Test ID prefix for Cypress/Jest testing.
   */
  testIdPrefix = input<string>('SharedDropdown');

  /**
   * Styling variant:
   * - 'pill' for rounded-full button (default)
   * - 'standard' for regular input styling with primary colors
   * - 'structure' for STRUCTURE card styling (gray border, navy hover)
   */
  variant = input<'pill' | 'standard' | 'structure'>('pill');

  /**
   * Custom border color (CSS color value or CSS variable).
   * If provided, overrides the variant's default border color.
   * @example 'var(--aca-blue)' or '#0066cc' or 'rgb(0, 102, 204)'
   */
  borderColor = input<string | undefined>(undefined);

  /**
   * Custom border radius (CSS value or Tailwind class).
   * If provided, overrides the variant's default border radius.
   * @example 'rounded-full' or 'rounded-sm' or '0.5rem'
   */
  borderRadius = input<string | undefined>(undefined);

  /**
   * Align the dropdown panel to the right edge of the trigger button.
   * By default, the panel aligns to the left edge.
   * Set to true to align to the right edge (useful for dropdowns near the right edge of the screen).
   * @default false
   */
  alignRight = input<boolean>(false);

  /**
   * Event emitted when user selects an option.
   * Emits the full option object.
   */
  selectionChanged = output<DropdownOption>();

  /**
   * Icons used in the component.
   */
  protected readonly icons = {
    chevronDown: new AcaFaIcon(faChevronDown),
    check: new AcaFaIcon(faCheck)
  };

  /**
   * Signal to track dropdown open/closed state.
   */
  protected readonly isOpen = signal(false);

  /**
   * Single merged class string for the trigger button.
   * Combines the static base class, open-state toggle, and optional Tailwind border-radius
   * utility into one [class] binding so the static class attribute is never overwritten.
   */
  protected readonly triggerClass = computed(() => {
    const classes = ['dropdown-trigger'];
    if (this.isOpen()) {
      classes.push('open');
    }
    const radius = this.borderRadius();
    if (radius?.startsWith('rounded')) {
      classes.push(radius);
    }
    return classes.join(' ');
  });

  /**
   * Computed signal that returns the display text for the currently selected option.
   */
  protected selectedDisplayText = computed(() => {
    const currentValue = this.selectedValue();
    const optionsList = this.options();
    const valueFnRef = this.valueFn();

    if (currentValue === null || currentValue === undefined) {
      return this.placeholder();
    }

    const selectedOption = optionsList.find(
      (opt) => valueFnRef(opt) === currentValue
    );

    if (selectedOption) {
      return this.displayFn()(selectedOption);
    }

    return this.placeholder();
  });

  /**
   * Toggle dropdown open/closed state.
   */
  protected toggleDropdown(): void {
    if (this.disabled()) return;
    this.isOpen.update((open) => !open);
  }

  /**
   * Close the dropdown.
   */
  protected closeDropdown(): void {
    this.isOpen.set(false);
  }

  /**
   * Handle option selection from dropdown.
   * @param option The selected option object.
   */
  protected selectOption(option: DropdownOption): void {
    if (this.disabled()) return;

    this.selectionChanged.emit(option);
    this.closeDropdown();
  }

  /**
   * Check if an option is currently selected.
   * @param option The option to check.
   * @returns True if the option is selected.
   */
  protected isSelected(option: DropdownOption): boolean {
    const currentValue = this.selectedValue();
    const valueFnRef = this.valueFn();
    return valueFnRef(option) === currentValue;
  }

  /**
   * Handle click outside to close dropdown.
   */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  /**
   * Handle keyboard events for accessibility.
   */
  @HostListener('document:keydown.escape')
  protected onEscapeKey(): void {
    this.closeDropdown();
  }
}

